import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import referentesService from "../../../../services/referentesService";
import { showToast } from "../../../../utils/showToast";

const TIPOS = ["comisionista", "externo", "estudiante", "docente", "administrativo"];
const TIPO_LABEL = { comisionista: "Comisionista", externo: "Externo", estudiante: "Estudiante", docente: "Docente", administrativo: "Administrativo" };
const PAGE_SIZES = [10, 25, 50];

export default function ComisionistasRankingView() {
  const navigate = useNavigate();
  const [rows, setRows]        = useState([]);
  const [loading, setLoading]  = useState(true);
  const [exporting, setExport] = useState(false);
  const [filtros, setFiltros]  = useState({ tipo: "", fecha_desde: "", fecha_hasta: "" });
  const [page, setPage]        = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.tipo)        params.tipo        = filtros.tipo;
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      const data = await referentesService.ranking(params);
      setRows(Array.isArray(data) ? data : []);
      setPage(1);
    } catch {
      showToast({ type: "danger", message: "Error al cargar el ranking." });
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const paginados  = useMemo(
    () => rows.slice((page - 1) * pageSize, page * pageSize),
    [rows, page, pageSize]
  );
  const goToPage = (n) => setPage(Math.min(Math.max(1, n), totalPages));

  const handleExport = async (formato) => {
    setExport(true);
    try {
      const params = { formato };
      if (filtros.tipo)        params.tipo        = filtros.tipo;
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      const blob = await referentesService.exportarRanking(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Ranking-Comisionistas.${formato === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast({ type: "danger", message: err?.message || "Error al exportar." });
    } finally {
      setExport(false);
    }
  };

  const totales = rows.reduce(
    (acc, r) => {
      acc.generado  += parseFloat(r.monto_total_generado || 0);
      acc.cobrado   += parseFloat(r.monto_cobrado || 0);
      acc.pendiente += parseFloat(r.monto_pendiente || 0);
      return acc;
    },
    { generado: 0, cobrado: 0, pendiente: 0 }
  );

  return (
    <section className="management-page management-theme-teal">
      <div className="container-fluid">
        <div className="management-card management-header mb-4">
          <div className="d-flex align-items-start flex-wrap gap-2 w-100">
            <div className="flex-grow-1">
              <button className="btn btn-link p-0 text-muted text-decoration-none small mb-1"
                onClick={() => navigate(-1)}>
                <i className="bi bi-chevron-left me-1"></i>Comisionistas
              </button>
              <div className="dashboard-eyebrow">Financiero / Comisionistas</div>
              <h5 className="mb-1">Ranking de comisionistas</h5>
              <p className="text-muted mb-0">Ordenado por monto total generado (bonificaciones no anuladas).</p>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary" disabled={exporting || loading}
                onClick={() => handleExport("excel")}>
                <i className="bi bi-file-earmark-excel me-1"></i>Excel
              </button>
              <button className="btn btn-outline-danger" disabled={exporting || loading}
                onClick={() => handleExport("pdf")}>
                <i className="bi bi-file-earmark-pdf me-1"></i>PDF
              </button>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="management-card mb-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-3">
              <label className="form-label small text-muted mb-1">Tipo</label>
              <select className="form-select" value={filtros.tipo}
                onChange={(e) => setFiltros((f) => ({ ...f, tipo: e.target.value }))}>
                <option value="">Todos los tipos</option>
                {TIPOS.map((t) => <option key={t} value={t}>{TIPO_LABEL[t]}</option>)}
              </select>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small text-muted mb-1">Desde</label>
              <input type="date" className="form-control" value={filtros.fecha_desde}
                onChange={(e) => setFiltros((f) => ({ ...f, fecha_desde: e.target.value }))} />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small text-muted mb-1">Hasta</label>
              <input type="date" className="form-control" value={filtros.fecha_hasta}
                onChange={(e) => setFiltros((f) => ({ ...f, fecha_hasta: e.target.value }))} />
            </div>
            <div className="col-12 col-md-3">
              <button className="btn btn-outline-secondary w-100"
                onClick={() => setFiltros({ tipo: "", fecha_desde: "", fecha_hasta: "" })}>
                Limpiar filtros
              </button>
            </div>
          </div>
        </div>

        {/* Cards resumen */}
        {!loading && rows.length > 0 && (
          <div className="row g-3 mb-3">
            {[
              { label: "Total generado",  value: totales.generado,  icon: "bi-coin",            color: "primary" },
              { label: "Total cobrado",   value: totales.cobrado,   icon: "bi-check-circle",    color: "success" },
              { label: "Total pendiente", value: totales.pendiente, icon: "bi-hourglass-split", color: "warning" },
            ].map((t) => (
              <div className="col-12 col-md-4" key={t.label}>
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-3 d-flex align-items-center gap-3">
                    <div className={`config-card-icon text-${t.color}`} style={{ background: `var(--bs-${t.color}-bg-subtle, #f0f0f0)` }}>
                      <i className={`bi ${t.icon}`}></i>
                    </div>
                    <div>
                      <div className="text-muted small">{t.label}</div>
                      <div className="fw-bold fs-5">${t.value.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabla */}
        <div className="management-card p-0">
          {loading ? (
            <div className="p-4 text-muted">Cargando ranking...</div>
          ) : rows.length === 0 ? (
            <div className="p-4 text-muted text-center">Sin datos para los filtros seleccionados.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th className="text-end">Bonificaciones</th>
                    <th className="text-end">Total generado</th>
                    <th className="text-end">Cobrado</th>
                    <th className="text-end">Pendiente</th>
                  </tr>
                </thead>
                <tbody>
                  {paginados.map((r, i) => (
                    <tr key={r.id}>
                      <td className="text-muted fw-bold">{(page - 1) * pageSize + i + 1}</td>
                      <td>
                        <button className="btn btn-link p-0 text-decoration-none fw-semibold"
                          onClick={() => navigate(`../${r.id}/perfil`)}>
                          {r.nombre}
                        </button>
                        {r.identificacion && <div className="text-muted small">{r.identificacion}</div>}
                      </td>
                      <td>
                        <span className="badge rounded-pill bg-primary-subtle text-primary">
                          {TIPO_LABEL[r.tipo] || r.tipo}
                        </span>
                      </td>
                      <td className="text-end">{r.total_bonificaciones}</td>
                      <td className="text-end fw-semibold">${parseFloat(r.monto_total_generado || 0).toFixed(2)}</td>
                      <td className="text-end text-success">${parseFloat(r.monto_cobrado || 0).toFixed(2)}</td>
                      <td className="text-end text-warning">${parseFloat(r.monto_pendiente || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Paginación */}
        {!loading && rows.length > 0 && (
          <div className="management-card d-flex justify-content-between align-items-center flex-wrap gap-3 mt-3">
            <div className="text-muted small">
              Mostrando {(page - 1) * pageSize + 1}–{Math.min(rows.length, page * pageSize)} de {rows.length}
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <select className="form-select form-select-sm" style={{ width: "auto" }}
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); goToPage(1); }}>
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} por página</option>)}
              </select>
              <div className="btn-group">
                <button className="btn btn-outline-secondary btn-sm"
                  onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                  <i className="bi bi-chevron-left"></i>
                </button>
                <button className="btn btn-outline-secondary btn-sm" disabled>
                  Página {page} / {totalPages}
                </button>
                <button className="btn btn-outline-secondary btn-sm"
                  onClick={() => goToPage(page + 1)} disabled={page >= totalPages}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
